// AdminTable hardcodes the .admin-table* classes, which live in the
// Administration page stylesheet.
// ponytail: importing it here is the cheap way to reuse them; the clean fix is
// to lift the shared .admin-table* rules into a styles/ partial.
import "../administration/administration.css";
import "./uploadSessions.css";

import { OPTIONS_SYSTEM, SESSION_STATUS } from "../constants";
import { RefreshButton, dateCell } from "../components/dataTable";
import {
  fetchCompanyContracts,
  resetCompanyContracts,
} from "../actions/company";
import { fetchSites, resetSites } from "../actions/sites";
import { useCallback, useEffect, useMemo, useState } from "react";

import { AdminTable } from "../administration/AdminTable";
import { EmptyState } from "../components/EmptyState";
import { LevelHeader } from "../clientDashboard/components/LevelHeader";
import PropTypes from "prop-types";
import { CfSelect as Select } from "../components/CfSelect";
import UploadSessionModal from "./uploadSessionModal";
import _ from "lodash";
import { addDangerAlert } from "../actions/alerts";
import { connect } from "react-redux";
import { fetchAllUploadSessions } from "../services/backendRequests";
import { fetchCompanies } from "../actions/companies";
import queryString from "query-string";
import { useDidMountEffect } from "../hooks/useDidMountEffect";
import { useLocation } from "react-router-dom";

// Pill tone per session status. Tones are roles, not statuses (see
// styles/_badges.scss), so the mapping is owned here, not by the stylesheet.
const STATUS_TONE = {
  [SESSION_STATUS.Initialized]: "muted",
  [SESSION_STATUS.InProgress]: "warn",
  [SESSION_STATUS.Completed]: "ok",
  [SESSION_STATUS.Synchronized]: "info",
  [SESSION_STATUS.Partial]: "danger",
};

const UploadSessions = ({
  user,
  companies,
  contracts,
  sites,
  fetchCompanies,
  fetchCompanyContracts,
  fetchSites,
  resetCompanyContracts,
  resetSites,
  addDangerAlert,
}) => {
  const location = useLocation();
  const { company, contract, site } = queryString.parse(location.search);
  const [companyId, setCompanyId] = useState(company);
  const [contractId, setContractId] = useState(contract);
  const [siteId, setSiteId] = useState(site);
  const [system, setSystem] = useState();
  const [status, setStatus] = useState(SESSION_STATUS.Completed);
  const [allSessions, setAllSessions] = useState([]);
  const [uploadSessionSelected, setUploadSessionSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // One derived list instead of four effects each rebuilding it from its own
  // subset of the criteria. Those effects all started with `if (elementId)`, so
  // clearing a select ran the effect, did nothing, and left the list filtered on
  // the value that had just been removed: the filter could be set and not unset
  // (R4-03). A criterion that is empty simply stops narrowing here.
  // `status` and `system` are not in this list: they are server-side query
  // params, so they drive the fetch instead.
  const visible = useMemo(
    () =>
      allSessions.filter(
        (session) =>
          (!companyId || session.company.id === companyId) &&
          (!contractId || session.contract_info.id === contractId) &&
          (!siteId || session.site.id === siteId)
      ),
    [allSessions, companyId, contractId, siteId]
  );

  const reloadData = useCallback(() => {
    setLoading(true);
    fetchAllUploadSessions(user, status, system)
      .then((data) => setAllSessions(_.sortBy(data, "created_at")))
      // A swallowed failure renders as "no session found", which reads as an
      // answer from the API instead of the absence of one.
      .catch(() =>
        addDangerAlert("Could not load the upload sessions. Please try again.")
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, system]);

  useEffect(() => {
    resetCompanyContracts();
    resetSites();
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  // Cascading pickers: a company loads its campaigns, a campaign loads its
  // sites. They feed the options of the select below them, nothing else.
  // Clearing the criterion resets that list: otherwise the select below keeps
  // offering the options of the scope that has just been removed.
  useEffect(() => {
    if (companyId) fetchCompanyContracts({ id: companyId });
    else resetCompanyContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (contractId) fetchSites({ id: contractId });
    else resetSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // Narrowing the scope invalidates what sits under it. useDidMountEffect, not
  // useEffect: a ?company=&contract= deep link must survive the first render.
  useDidMountEffect(() => {
    setContractId(undefined);
    setSiteId(undefined);
  }, [companyId]);

  useDidMountEffect(() => {
    setSiteId(undefined);
  }, [contractId]);

  const handleCloseModal = () => {
    setUploadSessionSelected(null);
    reloadData();
  };

  // isClearable is what puts the cross on the control; the derived list above is
  // what makes clearing actually widen the results. The value is looked up in
  // the options instead of being built by hand, so an empty criterion shows the
  // placeholder rather than a blank selected value.
  const selectComponent = (label, options, value, onChange, name = "name") => {
    const toOption = (option) => ({ value: option.id, label: option[name] });
    return (
      <label className="cf-field">
        <span className="cf-field__label">{label}</span>
        <Select
          className="cf-rs"
          classNamePrefix="cf-rs"
          isClearable
          isSearchable
          placeholder={`All ${label.toLowerCase()}`}
          options={options.map(toOption)}
          value={options.filter((o) => o.id === value).map(toOption)[0] ?? null}
          onChange={(option) => onChange(option?.value)}
        />
      </label>
    );
  };

  const columns = [
    {
      key: "session",
      label: "Session",
      cell: (us) => (
        <div className="us-identity">
          <span className="us-identity__name">{us.site.display_name}</span>
          <span className="us-identity__meta">
            {us.company.name} · {us.contract_info.name}
          </span>
        </div>
      ),
    },
    {
      key: "system",
      label: "System",
      cell: (us) =>
        OPTIONS_SYSTEM.find((o) => o.value === us.contract_info.system)?.label,
    },
    {
      key: "acquisition",
      label: "Acquisition",
      cell: (us) => dateCell(us.acquisition_date),
    },
    {
      key: "created",
      label: "Created by",
      cell: (us) => (
        <div className="us-identity">
          <span>{us.user}</span>
          <span className="us-identity__meta">{dateCell(us.created_at)}</span>
        </div>
      ),
    },
    {
      key: "updated",
      label: "Updated",
      cell: (us) => dateCell(us.updated_at),
    },
    {
      key: "status",
      label: "Status",
      cell: (us) => (
        <span className={`cf-badge cf-badge--${STATUS_TONE[us.status]}`}>
          {SESSION_STATUS[us.status]}
        </span>
      ),
    },
  ];

  const rows = visible;

  return (
    <>
      {uploadSessionSelected && (
        <UploadSessionModal
          us={uploadSessionSelected}
          isOpen={true}
          closeModal={() => handleCloseModal()}
          company={
            companies.filter(
              (c) => c.id === uploadSessionSelected?.company.id
            )[0]
          }
        />
      )}

      <div className="page-content container-fluid cf-page">
        <LevelHeader path={[{ label: "Upload Sessions" }]} />

        <div className="cf-filters">
          {selectComponent("Company", companies, companyId, (p) =>
            setCompanyId(p)
          )}
          {selectComponent("Campaign", contracts, contractId, (p) =>
            setContractId(p)
          )}
          {selectComponent(
            "Site",
            sites,
            siteId,
            (p) => setSiteId(p),
            "display_name"
          )}
          <label className="cf-field">
            <span className="cf-field__label">System</span>
            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isClearable
              isSearchable
              placeholder="All systems"
              options={OPTIONS_SYSTEM}
              value={OPTIONS_SYSTEM.find((o) => o.value === system) ?? null}
              onChange={(option) => setSystem(option?.value)}
            />
          </label>
          <label className="cf-field">
            <span className="cf-field__label">Status</span>
            {/* Not clearable, unlike the four others: the status is the scope
                of the server query, not a way of narrowing what came back.
                Emptying it would ask the API for every session of every
                status. */}
            <Select
              className="cf-rs"
              classNamePrefix="cf-rs"
              isSearchable
              options={SESSION_STATUS.iterateKeys().map((key) => ({
                value: key,
                label: SESSION_STATUS[key],
              }))}
              value={{ value: status, label: SESSION_STATUS[status] }}
              onChange={(option) => setStatus(option.value)}
            />
          </label>
          <span className="cf-filters__spacer" />
          <RefreshButton
            refreshing={loading}
            onRefresh={reloadData}
            data-tooltip-id="tooltip"
            data-tooltip-content="Refresh"
          />
        </div>

        {!loading && rows.length === 0 ? (
          <EmptyState
            icon="fa-solid fa-cloud-arrow-up"
            title="No upload session found."
            text="Try another status or widen the filters."
          />
        ) : (
          <AdminTable
            columns={columns}
            rows={rows}
            loading={loading}
            onRowClick={(us) => setUploadSessionSelected(us)}
          />
        )}
      </div>
    </>
  );
};

function mapStateToProps(state) {
  const companies = state.companies.all;
  const sortedCompanies = _.orderBy(
    companies,
    [(company) => company.name.toLowerCase()],
    ["asc"]
  );

  const contracts = state.company.contracts;
  const sortedContracts = _.orderBy(
    contracts,
    [(contract) => contract.name.toLowerCase()],
    ["asc"]
  );

  const sites = state.sites.all;
  const sortedSites = _.orderBy(
    sites,
    [(site) => site.display_name?.toLowerCase()],
    ["asc"]
  );

  return {
    user: state.user,
    companies: sortedCompanies,
    contracts: sortedContracts,
    sites: sortedSites,
  };
}

const callbacks = {
  fetchCompanies,
  fetchCompanyContracts,
  fetchSites,
  resetCompanyContracts,
  resetSites,
  addDangerAlert,
};

export default connect(mapStateToProps, callbacks)(UploadSessions);

UploadSessions.propTypes = {
  user: PropTypes.object.isRequired,
  companies: PropTypes.array.isRequired,
  contracts: PropTypes.array.isRequired,
  sites: PropTypes.array.isRequired,
  fetchCompanies: PropTypes.func.isRequired,
  fetchSites: PropTypes.func.isRequired,
  resetCompanyContracts: PropTypes.func.isRequired,
  resetSites: PropTypes.func.isRequired,
  addDangerAlert: PropTypes.func.isRequired,
};
